import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Clock, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { BRAND, PHONE_TEL, PHONE_DISPLAY } from "@/lib/constants";
import { Reveal } from "@/components/Reveal";
import SocialLinks from "@/components/SocialLinks";
import RgpdCheckbox from "@/components/RgpdCheckbox";

const inputCls =
  "w-full h-12 rounded-xl bg-[#141414] border border-white/10 px-4 text-sm text-white placeholder:text-zinc-600 focus:border-[#FF1E27] outline-none";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "Achat d'un véhicule", message: "" });
  const [rgpd, setRgpd] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!rgpd) return toast.error("Merci d'accepter le traitement de vos données (RGPD).");
    setSending(true);
    try {
      await api.post("/leads", { ...form, email: form.email || null, rgpd_consent: rgpd });
      toast.success("Message envoyé ! Nous revenons vers vous rapidement.");
      setForm({ name: "", phone: "", email: "", subject: "Achat d'un véhicule", message: "" });
      setRgpd(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-28 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <p className="font-spec text-xs uppercase tracking-[0.3em] text-[#FF1E27] mb-3">Contact</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight">
            Parlons de votre <span className="text-[#FF1E27]">projet auto</span>
          </h1>
        </Reveal>

        <div className="mt-12 grid gap-12 lg:grid-cols-5">
          <Reveal className="lg:col-span-2 space-y-6">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 space-y-5">
              <p className="flex items-start gap-3 text-sm text-zinc-300">
                <MapPin className="w-5 h-5 text-[#FF1E27] shrink-0" /> {BRAND.address}
              </p>
              <a href={`tel:${PHONE_TEL}`} data-testid="contact-phone-link" className="flex items-center gap-3 text-sm text-zinc-300 hover:text-white transition-colors">
                <Phone className="w-5 h-5 text-[#FF1E27] shrink-0" /> {PHONE_DISPLAY}
              </a>
              <p className="flex items-start gap-3 text-sm text-zinc-300">
                <Clock className="w-5 h-5 text-[#FF1E27] shrink-0" />
                Lun — Sam : 9h00 – 19h00 (sur rendez-vous le dimanche)
              </p>
            </div>
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6">
              <p className="font-spec text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Suivez le showroom</p>
              <SocialLinks />
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-3">
            <form onSubmit={submit} data-testid="contact-form" className="bg-[#141414] border border-white/10 rounded-2xl p-6 sm:p-8 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Nom complet *" className={inputCls} />
                <input data-testid="contact-phone-input" required value={form.phone} onChange={set("phone")} placeholder="Téléphone *" className={inputCls} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Email (optionnel)" className={inputCls} />
                <select data-testid="contact-subject-select" value={form.subject} onChange={set("subject")} className={inputCls}>
                  <option>Achat d'un véhicule</option>
                  <option>Revente / reprise</option>
                  <option>Demande d'essai</option>
                  <option>Autre demande</option>
                </select>
              </div>
              <textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")}
                placeholder="Votre message *"
                className="w-full rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-[#FF1E27] outline-none resize-none" />
              <RgpdCheckbox checked={rgpd} onChange={setRgpd} testid="contact-rgpd-checkbox" />
              <button data-testid="contact-form-submit-btn" type="submit" disabled={sending}
                className="h-12 px-8 rounded-full bg-[#FF1E27] text-white font-semibold flex items-center gap-2 hover:bg-[#E63946] transition-colors disabled:opacity-60">
                {sending && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer le message
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
